import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ArrowRight, Music, Loader2, Play, Pause, CheckCircle2, AlertCircle, Mic, MicOff, Edit3, Check, ShoppingCart, Download, Gift } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { CreateGiftDialog } from "@/components/CreateGiftDialog";

type Step = "event" | "genre" | "vocal" | "story" | "confirm" | "generating_text" | "review_lyrics" | "generating_music" | "result";

interface EventItem { id: string; name: string; emoji: string; code: string; }
interface GenreItem { id: string; name: string; emoji: string; code: string; suno_style: string | null; }
interface VocalItem { id: string; name: string; emoji: string; code: string; }

const LYRICS_PHRASES = [
  "👨‍🍳 Готовлю текст песни...",
  "🥣 Замешиваю ингредиенты...",
  "📝 Подбираю рифмы...",
  "✨ Добавляю магию...",
  "🎭 Ловлю вдохновение...",
  "🪄 Колдую над словами...",
];

const MUSIC_PHRASES = [
  "🎸 Настраиваю инструменты...",
  "🔥 Допекаю до хрустящей корочки...",
  "🎵 Сводим треки...",
  "🥁 Отбиваю ритм...",
  "🎹 Добавляю аранжировку...",
  "🎶 Шлифую звук...",
  "✨ Финальная подача...",
];

const AnimatedProgress = ({ phrases, durationMs }: { phrases: string[]; durationMs: number }) => {
  const [progress, setProgress] = useState(0);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const target = Math.min((elapsed / durationMs) * 90, 90);
      setProgress(target);
    }, 200);
    return () => clearInterval(interval);
  }, [durationMs]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex(prev => (prev + 1) % phrases.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [phrases.length]);

  return (
    <div className="text-center space-y-6 py-12">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Готовим ваш хит</h1>
        <p className="text-lg text-muted-foreground mt-3 min-h-[2rem] transition-all duration-500">
          {phrases[phraseIndex]}
        </p>
      </div>
      <div className="max-w-sm mx-auto space-y-2">
        <Progress value={progress} className="h-3" />
        <p className="text-sm font-medium text-primary">{Math.round(progress)}%</p>
      </div>
      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
        Не закрывайте страницу
      </div>
    </div>
  );
};

const CreateSong = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("event");
  const [events, setEvents] = useState<EventItem[]>([]);
  const [genres, setGenres] = useState<GenreItem[]>([]);
  const [vocals, setVocals] = useState<VocalItem[]>([]);

  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [selectedGenre, setSelectedGenre] = useState<GenreItem | null>(null);
  const [selectedVocal, setSelectedVocal] = useState<VocalItem | null>(null);
  const [story, setStory] = useState("");

  const [generationId, setGenerationId] = useState<string | null>(null);
  const [lyrics, setLyrics] = useState<string | null>(null);
  const [editedLyrics, setEditedLyrics] = useState<string>("");
  const [isEditingLyrics, setIsEditingLyrics] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<string>("pending");
  const [demoUrl, setDemoUrl] = useState<string | null>(null);
  const [demoUrl2, setDemoUrl2] = useState<string | null>(null);
  const [fullMusicUrl, setFullMusicUrl] = useState<string | null>(null);
  const [fullMusicUrl2, setFullMusicUrl2] = useState<string | null>(null);
  const [isFullPurchased, setIsFullPurchased] = useState(false);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [isBuying, setIsBuying] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTrack, setActiveTrack] = useState<1 | 2 | 3 | 4>(1);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showGiftDialog, setShowGiftDialog] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [totalGenerations, setTotalGenerations] = useState<number | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isStartingMusic, setIsStartingMusic] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micErr: any) {
        const msg = micErr?.name === 'NotAllowedError'
          ? 'Разрешите доступ к микрофону в настройках браузера'
          : micErr?.name === 'NotFoundError'
          ? 'Микрофон не найден на устройстве'
          : `Ошибка микрофона: ${micErr?.message || 'неизвестная ошибка'}`;
        toast({ title: 'Нет доступа к микрофону', description: msg, variant: 'destructive' });
        return;
      }

      // Pick a supported mimeType with fallback
      let mimeType = 'audio/webm';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
        } else {
          mimeType = '';
        }
      }

      const recorderOptions: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        if (blob.size < 1000) return;

        setIsTranscribing(true);
        try {
          const formData = new FormData();
          const ext = (mediaRecorder.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
          formData.append('audio', blob, `voice.${ext}`);

          const { data, error } = await supabase.functions.invoke('transcribe-voice', {
            body: formData,
          });

          if (error) throw error;
          if (data?.text) {
            setStory(prev => prev ? `${prev} ${data.text}` : data.text);
          }
        } catch (err: any) {
          toast({ title: 'Ошибка распознавания', description: err.message || 'Попробуйте ещё раз', variant: 'destructive' });
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err: any) {
      console.error('Recording error:', err);
      toast({ title: 'Ошибка записи', description: err?.message || 'Не удалось начать запись', variant: 'destructive' });
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      const [eventsRes, genresRes, vocalsRes] = await Promise.all([
        supabase.from("events").select("id, name, emoji, code").eq("is_active", true).order("sort_order"),
        supabase.from("genres").select("id, name, emoji, code, suno_style").eq("is_active", true).order("sort_order"),
        supabase.from("vocals").select("id, name, emoji, code").eq("is_active", true).order("sort_order"),
      ]);
      if (eventsRes.data) setEvents(eventsRes.data);
      if (genresRes.data) setGenres(genresRes.data);
      if (vocalsRes.data) setVocals(vocalsRes.data);
    };
    loadData();
  }, []);

  // Load balance
  useEffect(() => {
    if (!user) return;
    const loadBalance = async () => {
      const { data } = await supabase
        .from("web_users")
        .select("wow_hits_balance, total_generations")
        .eq("auth_user_id", user.id)
        .single();
      if (data) {
        setBalance(data.wow_hits_balance);
        setTotalGenerations(data.total_generations);
      }
    };
    loadBalance();
  }, [user]);

  // Poll generation status
  useEffect(() => {
    if (!generationId || generationStatus === "completed" || generationStatus === "failed") return;

    const interval = setInterval(async () => {
      const { data } = await supabase
        .from("generations")
        .select("status, demo_url, demo_url_2, lyrics, error_message, is_full_purchased, full_music_url, full_music_url_2, created_at")
        .eq("id", generationId)
        .single();

      if (data) {
        setGenerationStatus(data.status);

        // When lyrics are ready, move to review step
        if (data.status === "text_ready" && data.lyrics && step === "generating_text") {
          setLyrics(data.lyrics);
          setEditedLyrics(data.lyrics);
          setStep("review_lyrics");
        }

        if (data.demo_url) setDemoUrl(data.demo_url);
        if (data.demo_url_2) setDemoUrl2(data.demo_url_2);
        if (data.full_music_url) setFullMusicUrl(data.full_music_url);
        if (data.full_music_url_2) setFullMusicUrl2(data.full_music_url_2);
        if (data.is_full_purchased) setIsFullPurchased(true);
        if (data.created_at) setCreatedAt(data.created_at);
        if (data.lyrics && !lyrics) setLyrics(data.lyrics);

        if (data.status === "completed") {
          setStep("result");
        } else if (data.status === "failed") {
          setError(data.error_message || "Произошла ошибка при генерации");
          setStep("result");
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [generationId, generationStatus, step, lyrics]);

  const handleGenerate = async () => {
    if (!selectedEvent || !selectedGenre || !story.trim()) return;

    setStep("generating_text");
    setError(null);
    setGenerationStatus("generating_text");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const response = await supabase.functions.invoke("web-generate", {
        body: {
          event_id: selectedEvent.id,
          genre_id: selectedGenre.id,
          vocal_id: selectedVocal?.id || null,
          story: story.trim(),
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const result = response.data;

      if (!result.success) {
        if (result.code === "NO_HITS") {
          setError("У вас нет доступных хитов. Пополните баланс для генерации.");
          setStep("result");
          return;
        }
        throw new Error(result.error || "Generation failed");
      }

      setGenerationId(result.generation_id);

      // If lyrics returned directly, go to review
      if (result.lyrics && result.status === "text_ready") {
        setLyrics(result.lyrics);
        setEditedLyrics(result.lyrics);
        setGenerationStatus("text_ready");
        setStep("review_lyrics");
      }
    } catch (err: any) {
      console.error("Generation error:", err);
      setError(err.message || "Произошла ошибка");
      setStep("result");
    }
  };

  const handleStartMusic = async () => {
    if (!generationId) return;

    setIsStartingMusic(true);
    try {
      const lyricsChanged = editedLyrics !== lyrics;
      const response = await supabase.functions.invoke("web-start-music", {
        body: {
          generation_id: generationId,
          edited_lyrics: lyricsChanged ? editedLyrics : undefined,
        },
      });

      if (response.error) throw new Error(response.error.message);
      if (!response.data?.success) throw new Error(response.data?.error || "Failed");

      if (lyricsChanged) setLyrics(editedLyrics);
      setGenerationStatus("generating_music");
      setStep("generating_music");
    } catch (err: any) {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    } finally {
      setIsStartingMusic(false);
    }
  };

  const togglePlay = (trackNum: 1 | 2 | 3 | 4) => {
    const urlMap: Record<number, string | null> = {
      1: demoUrl,
      2: demoUrl2,
      3: fullMusicUrl,
      4: fullMusicUrl2,
    };
    const url = urlMap[trackNum];
    if (!url) return;

    if (audioRef) {
      audioRef.pause();
      audioRef.src = "";
    }

    if (isPlaying && activeTrack === trackNum) {
      setIsPlaying(false);
      return;
    }

    const audio = new Audio(url);
    audio.play();
    audio.onended = () => setIsPlaying(false);
    setAudioRef(audio);
    setActiveTrack(trackNum);
    setIsPlaying(true);
  };

  useEffect(() => {
    return () => {
      if (audioRef) {
        audioRef.pause();
        audioRef.src = "";
      }
    };
  }, [audioRef]);

  const wizardSteps: Step[] = ["event", "genre", "vocal", "story", "confirm"];
  const stepIndex = wizardSteps.indexOf(step);
  const totalSteps = 5;
  const isWizardStep = stepIndex >= 0;

  const goBack = () => {
    const idx = wizardSteps.indexOf(step);
    if (idx > 0) setStep(wizardSteps[idx - 1]);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress bar */}
          {isWizardStep && (
            <div className="mb-8">
              <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                <span>Шаг {Math.min(stepIndex + 1, totalSteps)} из {totalSteps}</span>
                {balance !== null && (
                  <span className="text-primary font-medium">⚡ {balance} хит{balance === 1 ? '' : balance < 5 ? 'а' : 'ов'}</span>
                )}
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* New user welcome banner */}
          {balance !== null && balance > 0 && totalGenerations === 0 && isWizardStep && (
            <div className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
              <p className="text-sm font-medium text-primary">
                🎁 У вас есть бесплатный хит! Создайте текст песни и получите 2 демо-версии — без оплаты.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Полную версию можно будет приобрести после прослушивания демо.
              </p>
            </div>
          )}

          {/* Step: Event */}
          {step === "event" && (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-display font-bold text-foreground">Выберите событие</h1>
                <p className="text-muted-foreground mt-1">Для какого повода будет песня?</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {events.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => { setSelectedEvent(e); setStep("genre"); }}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:border-primary hover:bg-primary/5 ${
                      selectedEvent?.id === e.id ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <span className="text-2xl">{e.emoji}</span>
                    <p className="mt-2 font-medium text-sm text-foreground">{e.name}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step: Genre */}
          {step === "genre" && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <button onClick={goBack} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-display font-bold text-foreground">Выберите жанр</h1>
                  <p className="text-muted-foreground mt-1">В каком стиле звучит ваш хит?</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {genres.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => { setSelectedGenre(g); setStep("vocal"); }}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:border-primary hover:bg-primary/5 ${
                      selectedGenre?.id === g.id ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <span className="text-2xl">{g.emoji}</span>
                    <p className="mt-2 font-medium text-sm text-foreground">{g.name}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step: Vocal */}
          {step === "vocal" && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <button onClick={goBack} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-display font-bold text-foreground">Выберите вокал</h1>
                  <p className="text-muted-foreground mt-1">Какой голос озвучит песню?</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {vocals.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => { setSelectedVocal(v); setStep("story"); }}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:border-primary hover:bg-primary/5 ${
                      selectedVocal?.id === v.id ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <span className="text-2xl">{v.emoji}</span>
                    <p className="mt-2 font-medium text-sm text-foreground">{v.name}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setSelectedVocal(null); setStep("story"); }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Пропустить →
              </button>
            </div>
          )}

          {/* Step: Story */}
          {step === "story" && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <button onClick={goBack} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-display font-bold text-foreground">Расскажите историю</h1>
                  <p className="text-muted-foreground mt-1">Опишите, о чём будет песня. Имена, факты, эмоции — всё пригодится!</p>
                </div>
              </div>
              <div className="relative">
                <Textarea
                  value={story}
                  onChange={(e) => setStory(e.target.value)}
                  placeholder="Например: Песня для Маши на 30-летие. Она обожает путешествия, у нас с ней куча смешных историй из поездки в Грузию..."
                  className="min-h-[180px] text-base pr-14"
                  maxLength={2000}
                  disabled={isTranscribing}
                />
                <button
                  type="button"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isTranscribing}
                  className={`absolute right-3 top-3 p-2.5 rounded-full transition-all ${
                    isRecording
                      ? "bg-destructive text-destructive-foreground animate-pulse"
                      : "bg-primary/10 text-primary hover:bg-primary/20"
                  } disabled:opacity-50`}
                  title={isRecording ? "Остановить запись" : "Голосовой ввод"}
                >
                  {isTranscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>
              </div>
              {isRecording && (
                <p className="text-sm text-destructive font-medium animate-pulse">
                  🎙 Запись... Нажмите на кнопку, чтобы остановить
                </p>
              )}
              {isTranscribing && (
                <p className="text-sm text-muted-foreground">
                  ✨ Распознаю речь...
                </p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{story.length}/2000</span>
                <Button
                  onClick={() => setStep("confirm")}
                  disabled={story.trim().length < 20 || isRecording || isTranscribing}
                  className="gap-2"
                >
                  Далее <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step: Confirm */}
          {step === "confirm" && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <button onClick={goBack} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-display font-bold text-foreground">Подтвердите заказ</h1>
                  <p className="text-muted-foreground mt-1">Проверьте параметры и запустите генерацию</p>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-5 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">Событие</span>
                  <span className="font-medium">{selectedEvent?.emoji} {selectedEvent?.name}</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">Жанр</span>
                  <span className="font-medium">{selectedGenre?.emoji} {selectedGenre?.name}</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">Вокал</span>
                  <span className="font-medium">{selectedVocal ? `${selectedVocal.emoji} ${selectedVocal.name}` : "Авто"}</span>
                </div>
                <div className="h-px bg-border" />
                <div>
                  <span className="text-muted-foreground text-sm">История</span>
                  <p className="mt-1 text-sm text-foreground leading-relaxed">{story}</p>
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground">Стоимость генерации</p>
                <p className="text-2xl font-bold text-primary mt-1">1 хит</p>
                {balance !== null && (
                  <p className="text-xs text-muted-foreground mt-1">На балансе: {balance} хит{balance === 1 ? '' : balance < 5 ? 'а' : 'ов'}</p>
                )}
              </div>

              <Button
                onClick={handleGenerate}
                className="w-full h-12 text-base font-bold gap-2"
                disabled={balance !== null && balance <= 0}
              >
                <Music className="w-5 h-5" />
                Создать хит 🔥
              </Button>

              {balance !== null && balance <= 0 && (
                <p className="text-center text-sm text-destructive">
                  Недостаточно хитов. Пополните баланс в личном кабинете.
                </p>
              )}
            </div>
          )}

          {/* Step: Generating Text */}
          {step === "generating_text" && (
            <AnimatedProgress phrases={LYRICS_PHRASES} durationMs={30000} />
          )}

          {/* Step: Review Lyrics */}
          {step === "review_lyrics" && (
            <div className="space-y-6 py-4">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h1 className="text-2xl font-display font-bold text-foreground">Текст готов!</h1>
                <p className="text-muted-foreground mt-1">Проверьте текст и при необходимости внесите правки</p>
              </div>

              <div className="bg-card rounded-xl border border-border p-5">
                {isEditingLyrics ? (
                  <Textarea
                    value={editedLyrics}
                    onChange={(e) => setEditedLyrics(e.target.value)}
                    className="min-h-[300px] text-sm font-sans leading-relaxed"
                  />
                ) : (
                  <pre className="text-sm text-foreground whitespace-pre-wrap font-sans leading-relaxed max-h-[400px] overflow-y-auto">
                    {editedLyrics}
                  </pre>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsEditingLyrics(!isEditingLyrics)}
                  className="gap-2"
                >
                  {isEditingLyrics ? (
                    <><Check className="w-4 h-4" /> Готово</>
                  ) : (
                    <><Edit3 className="w-4 h-4" /> Редактировать</>
                  )}
                </Button>
                <Button
                  onClick={handleStartMusic}
                  disabled={isStartingMusic || isEditingLyrics}
                  className="flex-1 h-12 text-base font-bold gap-2"
                >
                  {isStartingMusic ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Music className="w-5 h-5" />
                  )}
                  {isStartingMusic ? "Запускаю..." : "Делаем песню! 🚀"}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Generating Music */}
          {step === "generating_music" && (
            <AnimatedProgress phrases={MUSIC_PHRASES} durationMs={180000} />
          )}

          {/* Step: Result */}
          {step === "result" && (
            <div className="space-y-6 py-8">
              {error ? (
                <div className="text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10">
                    <AlertCircle className="w-8 h-8 text-destructive" />
                  </div>
                  <h1 className="text-2xl font-display font-bold text-foreground">Что-то пошло не так</h1>
                  <p className="text-muted-foreground">{error}</p>
                  <Button onClick={() => { setStep("confirm"); setError(null); }} variant="outline">
                    Попробовать снова
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    </div>
                    <h1 className="text-2xl font-display font-bold text-foreground">
                      {isFullPurchased ? "Полная версия получена! 🎉" : "Ваш хит готов! 🎉"}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                      {isFullPurchased ? "Скачайте полные версии вашего хита" : "Послушайте демо-версии"}
                    </p>
                  </div>

                  {/* Audio players */}
                  {isFullPurchased ? (
                    <div className="space-y-3">
                      {fullMusicUrl && (
                        <div
                          className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                            activeTrack === 3 && isPlaying ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                          }`}
                        >
                          <button
                            onClick={() => togglePlay(3)}
                            className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0"
                          >
                            {isPlaying && activeTrack === 3 ? (
                              <Pause className="w-5 h-5 text-primary" />
                            ) : (
                              <Play className="w-5 h-5 text-primary ml-0.5" />
                            )}
                          </button>
                          <div className="text-left flex-1">
                            <p className="font-medium text-foreground">Полная версия 1</p>
                            <p className="text-sm text-muted-foreground">~3 мин</p>
                          </div>
                          <a
                            href={fullMusicUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 hover:bg-primary/20 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="w-4 h-4 text-primary" />
                          </a>
                        </div>
                      )}
                      {fullMusicUrl2 && (
                        <div
                          className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                            activeTrack === 4 && isPlaying ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                          }`}
                        >
                          <button
                            onClick={() => togglePlay(4)}
                            className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0"
                          >
                            {isPlaying && activeTrack === 4 ? (
                              <Pause className="w-5 h-5 text-primary" />
                            ) : (
                              <Play className="w-5 h-5 text-primary ml-0.5" />
                            )}
                          </button>
                          <div className="text-left flex-1">
                            <p className="font-medium text-foreground">Полная версия 2</p>
                            <p className="text-sm text-muted-foreground">~3 мин</p>
                          </div>
                          <a
                            href={fullMusicUrl2}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 hover:bg-primary/20 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="w-4 h-4 text-primary" />
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {demoUrl && (
                        <button
                          onClick={() => togglePlay(1)}
                          className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                            activeTrack === 1 && isPlaying ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            {isPlaying && activeTrack === 1 ? (
                              <Pause className="w-5 h-5 text-primary" />
                            ) : (
                              <Play className="w-5 h-5 text-primary ml-0.5" />
                            )}
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-foreground">Версия 1</p>
                            <p className="text-sm text-muted-foreground">Демо ~40 сек</p>
                          </div>
                        </button>
                      )}

                      {demoUrl2 && (
                        <button
                          onClick={() => togglePlay(2)}
                          className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                            activeTrack === 2 && isPlaying ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            {isPlaying && activeTrack === 2 ? (
                              <Pause className="w-5 h-5 text-primary" />
                            ) : (
                              <Play className="w-5 h-5 text-primary ml-0.5" />
                            )}
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-foreground">Версия 2</p>
                            <p className="text-sm text-muted-foreground">Демо ~40 сек</p>
                          </div>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Buy Full Version CTA */}
                  {!isFullPurchased && fullMusicUrl && (
                    <div className="bg-card rounded-xl border-2 border-primary/30 p-5 space-y-3">
                      <div className="text-center">
                        <h3 className="font-display font-bold text-foreground text-lg">
                          🔥 Получите полную версию
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Полный трек ~3 мин без ограничений + скачивание
                        </p>
                      </div>
                      {(() => {
                        const hoursSince = createdAt
                          ? (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60)
                          : 0;
                        const hasDiscount = hoursSince >= 23;
                        const price = hasDiscount ? 199 : 249;
                        return (
                          <Button
                            onClick={async () => {
                              if (!generationId) return;
                              setIsBuying(true);
                              try {
                                const response = await supabase.functions.invoke("web-buy-full-track", {
                                  body: { generation_id: generationId },
                                });
                                if (response.error) throw new Error(response.error.message);
                                if (!response.data?.success) throw new Error(response.data?.error || "Payment failed");
                                window.location.href = response.data.payment_url;
                              } catch (err: any) {
                                if (err.message?.includes("Already purchased")) {
                                  setIsFullPurchased(true);
                                } else {
                                  toast({ title: "Ошибка оплаты", description: err.message, variant: "destructive" });
                                }
                              } finally {
                                setIsBuying(false);
                              }
                            }}
                            disabled={isBuying}
                            className="w-full h-14 text-lg font-bold gap-2"
                            size="lg"
                          >
                            {isBuying ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <ShoppingCart className="w-5 h-5" />
                            )}
                            {hasDiscount ? (
                              <>
                                Купить за <span className="line-through opacity-60 mr-1">249₽</span> {price}₽
                              </>
                            ) : (
                              `Купить полную версию — ${price}₽`
                            )}
                          </Button>
                        );
                      })()}
                    </div>
                  )}

                  {/* Gift Page Upsell */}
                  {isFullPurchased && (fullMusicUrl || fullMusicUrl2) && (
                    <div className="bg-card rounded-xl border-2 border-primary/20 p-5 space-y-3">
                      <div className="text-center">
                        <h3 className="font-display font-bold text-foreground text-lg">
                          🎁 Подарите эту песню красиво!
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Создайте персональную подарочную страницу с плеером, анимациями и возможностью поделиться
                        </p>
                      </div>
                      <Button
                        onClick={() => setShowGiftDialog(true)}
                        variant="outline"
                        className="w-full h-12 text-base font-semibold gap-2 border-primary/30 hover:bg-primary/5"
                      >
                        <Gift className="w-5 h-5" />
                        Создать подарочную страницу — 49₽
                      </Button>
                    </div>
                  )}

                  {showGiftDialog && generationId && (
                    <CreateGiftDialog
                      open={showGiftDialog}
                      onOpenChange={setShowGiftDialog}
                      generationId={generationId}
                      trackTitle={lyrics?.split("\n").find(l => l.trim().length > 0)?.trim() || "Персональный хит"}
                      audioUrl={fullMusicUrl!}
                      audioUrl2={fullMusicUrl2}
                    />
                  )}

                  {lyrics && (
                    <div className="bg-card rounded-xl border border-border p-5">
                      <h3 className="font-medium text-sm text-muted-foreground mb-2">Текст песни:</h3>
                      <pre className="text-sm text-foreground whitespace-pre-wrap font-sans leading-relaxed max-h-[300px] overflow-y-auto">{lyrics}</pre>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setStep("event");
                        setSelectedEvent(null);
                        setSelectedGenre(null);
                        setSelectedVocal(null);
                        setStory("");
                        setGenerationId(null);
                        setLyrics(null);
                        setEditedLyrics("");
                        setDemoUrl(null);
                        setDemoUrl2(null);
                        setFullMusicUrl(null);
                        setFullMusicUrl2(null);
                        setIsFullPurchased(false);
                        setCreatedAt(null);
                        setError(null);
                        setGenerationStatus("pending");
                        setIsEditingLyrics(false);
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Создать ещё
                    </Button>
                    <Button onClick={() => navigate("/account")} className="flex-1">
                      Личный кабинет
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CreateSong;
